import { parseISO, startOfHour } from 'date-fns';
import { format } from 'date-fns-tz';
import { LOCAL_TIMEZONE } from 'lib_ts/enums/datetime.enums';
import slugify from 'slugify';
import { v4 } from 'uuid';
import { TIMEZONES } from '../enums/datetime.enums';
import { WithID } from '../interfaces/common/i-generics';
import { ArrayHelper } from './array.helper';

type stringish = string | null | undefined;

export class StringHelper {
  static safeSort(a: stringish, b: stringish): number {
    try {
      return (a ?? '').localeCompare(b ?? '', 'en', {
        ignorePunctuation: true,
      });
    } catch (e) {
      console.error(e);
      return -1;
    }
  }

  static getTimezoneString = (config: { value: string; format: string }) => {
    const date = startOfHour(parseISO(config.value));
    const offsetHours = -date.getTimezoneOffset() / 60;
    const tz = TIMEZONES.find(
      (t) => t.offset === offsetHours && t.utc.includes(LOCAL_TIMEZONE)
    );

    return `${format(date, config.format)} ${tz?.abbr ?? offsetHours}`;
  };

  static hexToRgb = (hex?: string): number[] => {
    const black = [0, 0, 0];

    if (!hex) {
      return black;
    }

    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

    if (!result) {
      return black;
    }

    return [
      parseInt(result[1], 16),
      parseInt(result[2], 16),
      parseInt(result[3], 16),
    ];
  };

  static getContrastColor = (hex?: string): string => {
    const rgb = StringHelper.hexToRgb(hex);

    // Calculate relative luminance using the formula from WCAG
    // https://www.w3.org/TR/WCAG20/#relativeluminancedef
    const luminance = (0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2]) / 255;

    // Return semi-transparent white/black so it blends with the background
    // 0.65 opacity gives good readability while picking up background color
    return luminance > 0.5
      ? 'rgba(0, 0, 0, 0.65)' // Dark text with 65% opacity
      : 'rgba(255, 255, 255, 0.65)'; // Light text with 65% opacity
  };

  static capitalize(value: string, delimiter?: string): string {
    const parts = value.split(delimiter ?? ' ');

    return parts
      .map((p) => String(p).charAt(0).toUpperCase() + String(p).slice(1))
      .join(delimiter ?? ' ');
  }

  static classNames(values: stringish[]): string {
    return ArrayHelper.unique(values.filter((v) => v?.trim()))
      .join(' ')
      .trim();
  }

  // strips any accent characters and converts to lower case
  static normalize(value?: string) {
    if (!value) {
      return '';
    }

    return (
      value
        .normalize('NFD')
        // remove all accents
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
    );
  }

  static getInitials(name: stringish): string {
    const splitNames = (name ?? '').split(' ').map((n) =>
      slugify(n, {
        strict: true,
        trim: true,
        replacement: '',
      })
    );

    return splitNames
      .filter(
        // only keep initial from first and final parts
        (_, i) => i === 0 || i === splitNames.length - 1
      )
      .map(
        // transform each letter
        (s) => (s ? s.substring(0, 1).toUpperCase() : '?')
      )
      .join('');
  }

  // first initial, then the rest of the name: "C. Tall Guy", "V. Guerrero Jr."
  static shortenName(name: stringish): string {
    const parts = (name ?? '').split(' ').filter((part) => part.length > 0);

    if (parts.length <= 1) {
      return name || '';
    }

    const slug = slugify(parts[0], {
      strict: true,
      trim: true,
      replacement: '',
    });
    const firstInitial = slug ? slug.substring(0, 1).toUpperCase() : '?';

    return `${firstInitial}. ${parts.slice(1).join(' ')}`;
  }

  // returns the final section of a v4 uuid, which should be "unique enough"
  static getCollectionID(): string {
    return v4().split('-').slice(-1)[0];
  }

  static getEmailDomain = (email?: string) => {
    try {
      if (!email) {
        throw new Error('Invalid email: cannot be empty');
      }

      const atIndex = email.lastIndexOf('@');
      if (atIndex === -1) {
        throw new Error('Invalid email: does not contain "@" symbol');
      }

      return email.substring(atIndex + 1);
    } catch (e) {
      console.error(e);
      return '';
    }
  };

  // e.g. file.mp4 => mp4, use fallback if "." cannot be found
  static getFileExtension = (path: string, fallback: string) => {
    try {
      const index = path.lastIndexOf('.');

      if (index === -1) {
        throw new Error('Failed to find "." for extension extraction');
      }

      return path.substring(index + 1);
    } catch (e) {
      console.error(e);
      return fallback;
    }
  };

  // takes an array of objects and assigns one of it's fields to _id
  // helpful for ensuring non mongo documents have a unique identifier
  static addIds = <T extends Record<string, any>, K extends keyof T>(
    items: T[],
    field: K
  ): WithID<T>[] =>
    items.map((item) => ({
      ...item,
      _id: String(item[field]), // just in case the field isn't a string
    }));
}
